<template>
  <div class="form-card">
    <p class="form-card__title">dream meet <span class="dream-meet-beta">Beta</span></p>
    <p class="dream-meet__lede">
      Teach on TDI's own video meetings. Create a link once and use it for every class:
      it becomes your class Meeting Link, and your student sees it on My Class.
    </p>
    <p v-if="!origin" class="dream-meet__note">dream meet is being prepared.</p>
    <template v-else>
      <p v-if="isMeetLink" class="dream-meet__link">
        <span class="info-label">Your dream meet link</span>
        <a :href="currentLink" target="_blank" rel="noopener noreferrer">{{ currentLink }}</a>
      </p>
      <div v-if="confirming" class="dream-meet__confirm" role="alertdialog" aria-labelledby="dream-meet-confirm">
        <p id="dream-meet-confirm">
          This replaces your current Meeting Link<template v-if="currentLink">, <strong>{{ currentLink }}</strong></template>.
          Your student will use the new link from now on.
        </p>
        <div class="dream-meet__confirm-actions">
          <button class="btn btn-ghost" type="button" @click="confirming = false">Cancel</button>
          <button class="btn btn-primary" type="button" @click="create">Replace link</button>
        </div>
      </div>
      <p v-if="error" class="dream-meet__error" role="alert">{{ error }}</p>
    </template>
    <div class="form-actions">
      <a v-if="isMeetLink" class="btn btn-primary" :href="currentLink" target="_blank" rel="noopener noreferrer">Open dream meet</a>
      <button class="btn" :class="isMeetLink ? 'btn-ghost' : 'btn-primary'" type="button"
              :disabled="!origin || busy || confirming" @click="request">
        {{ busy ? 'Creating…' : isMeetLink ? 'Create a new link' : 'Create dream meet link' }}
      </button>
    </div>
    <p class="dream-meet-beta-notice">dream meet is in beta. Keep your Zoom or Google Meet link handy in case a class needs it.</p>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, ref } from 'vue';
import { createDreamMeeting } from '@/api/meet';
import { updateVideoLink } from '@/api';
import { dreamMeetOrigin, isDreamMeetLink } from '@/domain/dreamMeet';

const props = defineProps({ currentLink: { type: String, default: '' } });
const emit = defineEmits(['updated']);
const origin = dreamMeetOrigin(process.env.VUE_APP_MEET_URL);
const isMeetLink = computed(() => isDreamMeetLink(origin, props.currentLink));
const busy = ref(false);
const confirming = ref(false);
const error = ref('');

// Replacing a saved link changes where the student goes, so ask first.
function request() {
    error.value = '';
    if (props.currentLink) confirming.value = true;
    else create();
}

async function create() {
    confirming.value = false;
    busy.value = true;
    error.value = '';
    try {
        const url = await createDreamMeeting(origin);
        try {
            await updateVideoLink(url);
        } catch (cause) {
            throw new Error(cause?.response?.data?.message || 'The meeting was created, but your Meeting Link could not be saved. Please try again.');
        }
        emit('updated', url);
        alert('Meeting Link updated to your new dream meet link.');
    } catch (cause) {
        error.value = cause instanceof Error ? cause.message : 'Could not create a dream meet link. Please try again.';
    } finally {
        busy.value = false;
    }
}
</script>

<style scoped>
.dream-meet__lede { margin: .5rem 0 0; color: var(--ink-2); }
.dream-meet__note { margin-top: .75rem; color: var(--ink-2); }
.dream-meet__link { display: flex; flex-direction: column; gap: .25rem; margin-top: 1rem; overflow-wrap: anywhere; }
.dream-meet__confirm { margin-top: 1rem; padding: 1rem 1.25rem; border: 1px solid var(--gold); border-radius: 12px; background: var(--gold-soft); }
.dream-meet__confirm p { margin: 0; overflow-wrap: anywhere; }
.dream-meet__confirm-actions { display: flex; justify-content: flex-end; flex-wrap: wrap; gap: .65rem; margin-top: .75rem; }
.dream-meet__error { margin-top: .75rem; color: #da1e28; }
/* Same badge and notice as LiveTranslationEntry.vue. */
.dream-meet-beta {
  display: inline-block;
  margin-left: 0.5rem;
  padding: 0.125rem 0.5rem;
  border-radius: 0.375rem;
  background: #fef3c7;
  color: #92400e;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: normal;
  text-transform: none;
  vertical-align: middle;
}
.dream-meet-beta-notice {
  margin-top: 0.75rem;
  color: #64748b;
  font-size: 0.75rem;
}
</style>
